import React from 'react';

import { push } from 'connected-react-router';
import { connect, useDispatch, useSelector } from 'react-redux';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { UIMenus } from '@webapp/store/ui/types';
import {
  AddNewAccount,
  DeleteAdditionalAccount,
} from '@webapp/store/user/action-creators';

import Box from '@components/layout/box';
import Tag from '@components/basics/tag';
import BoxHeader from '@components/layout/box-header';
import ButtonGroup from '@components/basics/button-group';

import EmailOrPhoneCtrl from '@webapp/controllers/form/email-or-phone';

import { ContactMenuEditor } from './contact-editor-menu';
import styled from 'styled-components';
import * as user from '@webapp/sdk/users';
import Typography from '@components/basics/typography';

interface UserEmailEditorProps {
  className?: string;
  hasPassword: boolean;
  openedMenu: UIMenus;
}

const mapStateToProps = (state: AppState) => ({
  hasPassword: !!state.auth.user?.attributes,
  openedMenu: state.ui.menuOpened,
});

export const UserEmailEditor: React.FC<UserEmailEditorProps> = ({
  className,
  hasPassword,
  openedMenu,
}) => {
  const dispatch = useDispatch();

  const [newEmail, setNewEmail] = React.useState('');
  const [showEmailInUseErrorMessage, SetShowEmailInUseErrorMessage] =
    React.useState<boolean>(false);

  const primaryEmail = useSelector(
    (state: AppState) =>
      state.user.profile?.email || state.auth.user?.attributes?.email || ''
  );
  const primaryEmailVerified = useSelector(
    (state: AppState) => !!state.auth.user?.attributes?.email_verified || false
  );

  const additionalEmails = useSelector(
    (state: AppState) => state.user.profile?.additionalEmails || []
  );

  const handleAddNewEmail = async () => {
    const isInUse = await user.checkEmailInUse(newEmail);
    if (!isInUse.isEmailExists) {
      SetShowEmailInUseErrorMessage(false);
      dispatch(new AddNewAccount({ type: 'email', address: newEmail }));
      dispatch(
        hasPassword
          ? push('/user/confirm-changes')
          : push('/user/create-password')
      );
    } else {
      SetShowEmailInUseErrorMessage(true);
    }
  };

  return (
    <Box className={className} noBorderTop noBorderBottom>
      <BoxHeader
        title={<Translate id="USER_PROFILE.GENERAL.EMAIL.TITLE" />}
        text={<Translate id="USER_PROFILE.GENERAL.EMAIL.SUBTITLE" />}
      />
      <EmailOrPhoneCtrl
        className="email-input mb-10"
        type="email"
        value={newEmail}
        label={<Translate id="USER_PROFILE.GENERAL.EMAIL.ADD_NEW" />}
        onChange={(contact) => setNewEmail(contact.value)}
        decoration={
          showEmailInUseErrorMessage ? (
            <StyledError>
              <Translate id="APIError.EmailAlreadyInUse" />
            </StyledError>
          ) : null
        }
        onInputChange={() => SetShowEmailInUseErrorMessage(false)}
      />
      {newEmail !== '' && (
        <ButtonGroup
          className="mb-10"
          onChange={handleAddNewEmail}
          onCancel={() => setNewEmail('')}
        />
      )}
      {primaryEmail ? (
        <EmailOrPhoneCtrl
          className={`email-input ${!primaryEmailVerified ? '' : 'mb-10'}`}
          type="email"
          locked
          value={primaryEmail}
          needsValidation={!primaryEmailVerified}
          isPrimary
          decoration={
            <Tag>
              <Translate id="USER_PROFILE.GENERAL.EMAIL_PHONE.TAG" />
            </Tag>
          }
          onChange={() => null}
        />
      ) : null}
      {additionalEmails?.map((account) => (
        <EmailOrPhoneCtrl
          key={account.value}
          className={`email-input ${!account.verified ? '' : 'mb-10'} ${
            openedMenu === `USER-EDITOR-MENU-${account.value}` ? 'opened' : ''
          }`}
          type="email"
          locked
          value={account.value}
          needsValidation={!account.verified}
          decoration={
            <ContactMenuEditor
              id={account.value}
              onDelete={() => dispatch(new DeleteAdditionalAccount(account))}
            />
          }
          onChange={() => null}
        />
      ))}
    </Box>
  );
};

export default connect(mapStateToProps)(styled(UserEmailEditor)`
  .opened {
    z-index: ${({ theme }) => theme.zIndices.dialogs};
  }

  .email-input {
    .decoration-right {
      top: 7px;
      transform: none;

      @media ${({ theme }) => theme.device.laptopXL} {
        top: 12px;
      }
    }

`);

const StyledError = styled(Typography).attrs(() => ({
  type: 'small',
  weight: '700',
}))`
  text-align: center;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.error};
`;
